import type { ServiceResult } from "@core/services/serviceResult";

import httpClient from "@common/http-client";
import { resolveServiceErrorKey } from "@common/utils/http/errors";
import { toOptionalString } from "@common/utils/strings";

export type SignInStep = 1 | 2;

export interface SignInState {
  step: SignInStep;
  identifier: string;
  tfaChannel: string;
  tfaChallenge: string | null;
  redirectUrl: string;
  errorKey?: string;
  siteKey?: string;
  tfaCode?: string;
  completed: boolean;
  successRedirectUrl?: string;
}

interface RawSignInResponse {
  step?: number;
  identifier?: unknown;
  phoneNumber?: unknown;
  tfaChannel?: unknown;
  tfaChallenge?: unknown;
  redirectUrl?: unknown;
  errorKey?: unknown;
  siteKey?: unknown;
  tfaCode?: unknown;
  completed?: unknown;
  successRedirectUrl?: unknown;
}

const defaultRedirectUrl = "/account";
const genericErrorKey = "signIn.errors.generic";
const defaultTfaChannel = "Sms";
const isEmailIdentifier = (value: string) => value.includes("@");

const normalizeSignInResponse = (raw: RawSignInResponse | undefined): SignInState => {
  const step = raw?.step === 2 ? 2 : 1;
  const identifier = toOptionalString(raw?.identifier) ?? toOptionalString(raw?.phoneNumber) ?? "";
  const tfaChannel = toOptionalString(raw?.tfaChannel) ?? defaultTfaChannel;
  const tfaChallenge = toOptionalString(raw?.tfaChallenge) ?? null;
  const redirectUrl = toOptionalString(raw?.redirectUrl) ?? defaultRedirectUrl;
  const errorKey = toOptionalString(raw?.errorKey);
  const siteKey = toOptionalString(raw?.siteKey);
  const tfaCode = toOptionalString(raw?.tfaCode) ?? "";
  const completed = raw?.completed === true;
  const successRedirectUrl = toOptionalString(raw?.successRedirectUrl);

  return {
    step: step,
    identifier,
    tfaChannel,
    tfaChallenge,
    redirectUrl,
    errorKey,
    siteKey,
    tfaCode,
    completed,
    successRedirectUrl
  };
};

const buildResult = (state: SignInState): ServiceResult<SignInState> => {
  if (state.errorKey) {
    return { success: false, data: state, errorKey: state.errorKey };
  }
  return { success: true, data: state };
};

const ensureAntiForgeryToken = () => {
  if (!httpClient.getAntiForgeryToken()) {
    httpClient.setDefaultAntiForgeryToken();
  }
};

export const authService = {
  ensureAntiForgeryToken() {
    ensureAntiForgeryToken();
  },

  async signOut(): Promise<void> {
    ensureAntiForgeryToken();
    await httpClient.post("/api/account/sign-out", {});
  },

  async getState(): Promise<ServiceResult<SignInState>> {
    try {
      const response = await httpClient.get<RawSignInResponse>("/dashboard?handler=SignInState");
      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown, genericErrorKey) };
      }
      return buildResult(normalizeSignInResponse(response.data));
    } catch (error) {
      return { success: false, errorKey: resolveServiceErrorKey(error, genericErrorKey) };
    }
  },

  async requestTfaCode(payload: {
    identifier: string;
    redirectUrl?: string;
    cfTurnstileResponse: string;
  }): Promise<ServiceResult<SignInState>> {
    try {
      const isEmail = isEmailIdentifier(payload.identifier);
      const response = await httpClient.post<RawSignInResponse>("/dashboard?handler=ReceiveTfaCode", {
        identifier: payload.identifier,
        ...(isEmail ? {} : { phoneNumber: payload.identifier }),
        redirectUrl: payload.redirectUrl ?? defaultRedirectUrl,
        cfTurnstileResponse: payload.cfTurnstileResponse
      });

      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown, genericErrorKey) };
      }

      return buildResult(normalizeSignInResponse(response.data));
    } catch (error) {
      return { success: false, errorKey: resolveServiceErrorKey(error, genericErrorKey) };
    }
  },

  async confirmTfaCode(payload: {
    identifier: string;
    tfaCode: string;
    tfaChallenge?: string | null;
    redirectUrl?: string;
  }): Promise<ServiceResult<SignInState>> {
    try {
      const isEmail = isEmailIdentifier(payload.identifier);
      const response = await httpClient.post<RawSignInResponse>("/dashboard?handler=ConfirmTfaCode", {
        identifier: payload.identifier,
        ...(isEmail ? {} : { phoneNumber: payload.identifier }),
        tfaCode: payload.tfaCode,
        tfaChallenge: payload.tfaChallenge ?? null,
        redirectUrl: payload.redirectUrl ?? defaultRedirectUrl
      });

      if (!response.ok) {
        return { success: false, errorKey: resolveServiceErrorKey(response.errorThrown, genericErrorKey) };
      }

      return buildResult(normalizeSignInResponse(response.data));
    } catch (error) {
      return { success: false, errorKey: resolveServiceErrorKey(error, genericErrorKey) };
    }
  }
};
